import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  TrendingUp,
  Users,
  UserPlus,
  Link as LinkIcon,
  Palette,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  Smartphone,
  Moon,
  Sun,
  Globe
} from 'lucide-react';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { logout } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('theme') === 'dark' || 
      (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => setIsDarkMode(prev => !prev);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setShowLangMenu(false);
  };

  const currentLang = i18n.language?.substring(0, 2) || 'fr';
  const langLabels: Record<string, string> = { fr: 'FR', en: 'EN', es: 'ES' };

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setShowInstallBanner(false);
    }
  };

  const sidebarLinks = [
    { name: t('nav.dashboard'), icon: LayoutDashboard, path: '/admin/dashboard' },
    { name: t('nav.insights'), icon: TrendingUp, path: '/admin/insights' },
    { name: t('nav.clients'), icon: Users, path: '/admin/clients' },
    { name: t('nav.team'), icon: UserPlus, path: '/admin/team' },
    { name: t('nav.integrations'), icon: LinkIcon, path: '/admin/integrations' },
    { name: t('nav.branding'), icon: Palette, path: '/admin/branding' },
    { name: t('nav.pricing'), icon: CreditCard, path: '/admin/pricing' },
    { name: t('nav.settings'), icon: Settings, path: '/admin/settings' },
  ];

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex transition-colors duration-300">
      {/* Sidebar Desktop */}
      <aside className="hidden lg:flex w-64 bg-white dark:bg-slate-900 border-r dark:border-slate-800 flex-col sticky top-0 h-screen overflow-y-auto">
        <div className="p-8">
            <h1 className="text-2xl font-black text-gray-900 dark:text-white tracking-tighter">
                Que<span className="text-blue-600">lyo</span>
            </h1>
        </div>
        
        <nav className="flex-1 px-4 space-y-1">
          {sidebarLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 group ${
                  isActive 
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold' 
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800'
                }`}
              >
                <link.icon className={`w-5 h-5 mr-3 transition-transform group-hover:scale-110 ${isActive ? 'text-blue-600 dark:text-blue-400' : ''}`} />
                <span className="text-sm tracking-tight">{link.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t dark:border-slate-800 space-y-2">
          {/* Language Selector */}
          <div className="relative">
            <button 
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center w-full px-4 py-3 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition-all duration-200"
            >
              <Globe className="w-5 h-5 mr-3" />
              <span className="text-sm font-bold tracking-tight">{langLabels[currentLang] || 'FR'}</span>
            </button>
            {showLangMenu && (
              <div className="absolute bottom-full left-0 mb-1 w-full bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl shadow-lg overflow-hidden z-50">
                {Object.entries(langLabels).map(([code, label]) => (
                  <button
                    key={code}
                    onClick={() => changeLanguage(code)}
                    className={`w-full px-4 py-2.5 text-left text-sm font-bold transition-colors ${
                      currentLang === code 
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600' 
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button 
            onClick={toggleDarkMode}
            className="flex items-center w-full px-4 py-3 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition-all duration-200"
          >
            {isDarkMode ? <Sun className="w-5 h-5 mr-3" /> : <Moon className="w-5 h-5 mr-3" />}
            <span className="text-sm font-bold tracking-tight">{isDarkMode ? t('nav.lightMode') : t('nav.darkMode')}</span>
          </button>
          
          <button 
            onClick={logout}
            className="flex items-center w-full px-4 py-3 text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/10 hover:text-red-600 dark:hover:text-red-400 rounded-xl transition-all duration-200"
          >
            <LogOut className="w-5 h-5 mr-3" />
            <span className="text-sm font-bold tracking-tight">{t('auth.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay Menu */}
      {isMobileMenuOpen && (
        <div 
            className="fixed inset-0 z-[100] lg:hidden animate-in fade-in duration-300"
        >
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={closeMobileMenu} />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-white dark:bg-slate-900 shadow-2xl animate-in slide-in-from-left duration-300 flex flex-col">
                <div className="p-6 flex justify-between items-center border-b dark:border-slate-800">
                    <h1 className="text-xl font-black text-gray-900 dark:text-white tracking-tighter">
                        Que<span className="text-blue-600">lyo</span>
                    </h1>
                    <button onClick={closeMobileMenu} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none">
                        <X className="w-6 h-6" />
                    </button>
                </div>
                <nav className="flex-1 p-4 space-y-1">
                    {sidebarLinks.map((link) => {
                        const isActive = location.pathname === link.path;
                        return (
                            <Link
                                key={link.path}
                                to={link.path}
                                onClick={closeMobileMenu}
                                className={`flex items-center px-4 py-4 rounded-xl transition-all duration-200 ${
                                    isActive 
                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold' 
                                        : 'text-gray-500 dark:text-gray-400'
                                }`}
                            >
                                <link.icon className="w-6 h-6 mr-4" />
                                <span className="text-lg tracking-tight">{link.name}</span>
                            </Link>
                        );
                    })}
                </nav>
                <div className="p-6 border-t dark:border-slate-800 space-y-4">
                    {/* Mobile Language Selector */}
                    <div className="flex gap-2">
                      {Object.entries(langLabels).map(([code, label]) => (
                        <button
                          key={code}
                          onClick={() => changeLanguage(code)}
                          className={`flex-1 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${
                            currentLang === code
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <button 
                        onClick={logout}
                        className="flex items-center w-full text-red-600 font-black uppercase tracking-widest text-xs"
                    >
                        <LogOut className="w-5 h-5 mr-3" />
                        {t('auth.logout')}
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* Mobile Header */}
      <div className="flex-1 flex flex-col min-h-screen relative">
        {/* PWA Install Banner */}
        {showInstallBanner && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[150] bg-gray-900 text-white p-4 rounded-2xl shadow-2xl flex items-center gap-4 animate-in slide-in-from-bottom duration-500 max-w-sm w-[90vw]">
            <div className="bg-blue-600 p-2 rounded-lg">
                <Smartphone className="w-6 h-6" />
            </div>
            <div className="flex-1 text-left">
                <p className="text-sm font-bold leading-tight">{t('common.installApp')}</p>
                <p className="text-[10px] text-gray-400">{t('common.installDescription')}</p>
            </div>
            <button onClick={handleInstallClick} className="bg-white text-gray-900 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest active:scale-95 transition-transform">
                {t('common.install')}
            </button>
            <button onClick={() => setShowInstallBanner(false)} className="p-1 hover:bg-gray-800 rounded transition-colors text-gray-500">
                <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <header className="lg:hidden h-16 bg-white dark:bg-slate-900 border-b dark:border-slate-800 flex items-center justify-between px-6 sticky top-0 z-50">
            <button 
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2 -ml-2 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors"
            >
                <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-black text-gray-900 dark:text-white tracking-tighter absolute left-1/2 -translate-x-1/2">
                Que<span className="text-blue-600">lyo</span>
            </h1>
            <button 
                onClick={toggleDarkMode}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors"
            >
                {isDarkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
            </button>
        </header>

        <main className="flex-1 relative overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}
